<script setup lang="ts">
  import { watch, nextTick } from 'vue'
  import { useForm } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import {
    useCustomFieldsContext,
    validateCustomFieldValues
  } from '../composables/useCustomFields'
  import CustomFieldInput from './CustomFieldInput.vue'

  const model = defineModel<Record<string, unknown>>({ default: () => ({}) })

  const ctx = useCustomFieldsContext()

  const form = useForm({
    defaultValues: { ...model.value } as Record<string, unknown>
  })

  /**
   * Validates all custom fields and returns true if valid.
   * Call from the parent form's onSubmit to gate submission.
   */
  async function validate(): Promise<boolean> {
    if (!ctx) return true
    if (form.validateAllFields) {
      await form.validateAllFields('blur')
    }
    return validateCustomFieldValues(ctx.customFields.value, model.value)
  }

  defineExpose({ validate })

  let syncing = false

  const formValues = form.useStore((s) => s.values)
  watch(
    formValues,
    (values) => {
      syncing = true
      model.value = { ...values }
      nextTick(() => {
        syncing = false
      })
    },
    { deep: true }
  )

  watch(
    model,
    (newValues) => {
      if (syncing) return
      form.reset(newValues as Record<string, unknown>)
    },
    { deep: true }
  )
</script>

<template>
  <FieldGroup v-if="ctx && ctx.customFields.value.length > 0">
    <CustomFieldInput
      v-for="field in ctx.customFields.value"
      :key="field.fieldName"
      :field="field"
      :form="form"
    />
  </FieldGroup>
</template>
